import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import {
    BusinessInput,
    EditButtons,
    PourAvailabilityInput,
} from '@barboxx/admin-components';
import { BUSINESS_POUR_AVAILABILITY_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessPourAvailability } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    pourAvailability: BusinessPourAvailability.DetailsResponse;
}

export const PourAvailabilityDetailsCard = ({ pourAvailability }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutatePourAvailability = useMutation({
        mutationKey: ['businessPourAvailability', pourAvailability.id],
        mutationFn: (data: BusinessPourAvailability.UpdateRequest) =>
            Api.BusinessSpecific.PourAvailability.putPourAvailability(
                pourAvailability.id,
                data
            ),
    });

    const formContext = useForm<BusinessPourAvailability.UpdateRequest>({
        resolver: yupResolver(BUSINESS_POUR_AVAILABILITY_SCHEMA),
        defaultValues: pourAvailability,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: BusinessPourAvailability.UpdateRequest) => {
        mutatePourAvailability
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated business pour availability!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show(
                    'Error Updating business pour availability!',
                    {
                        severity: 'error',
                    }
                );
            });
    };

    return (
        <Card>
            <CardHeader title="PourAvailability Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <BusinessInput disabled={!editable} />
                        <PourAvailabilityInput disabled={!editable} />

                        <TextFieldElement
                            label="Base Price"
                            name="basePrice"
                            type="number"
                            disabled={!editable}
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                        <TextFieldElement
                            label="Up Charge"
                            name="upCharge"
                            type="number"
                            disabled={!editable}
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                        <TextFieldElement
                            label="DIsplay Order"
                            name="displayOrder"
                            type="number"
                            disabled={!editable}
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                    step: 1,
                                },
                            }}
                        />
                        <CheckboxElement
                            label="Not Allowed"
                            name="notAllowed"
                            disabled={!editable}
                            onChange={(e, value) => {
                                if (value === true) {
                                    formContext.setValue('basePrice', 0);
                                    formContext.setValue('upCharge', 0);
                                }
                            }}
                        />
                        <CheckboxElement
                            label="Active"
                            name="isActive"
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Default"
                            name="isDefault"
                            disabled={!editable}
                        />
                        <Can I="update" a="PourAvailability">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
